import React, { useMemo } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import SectionCard from '../components/SectionCard';

const fmtNum = (n, d = 0) => Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: d });
const fmtDate = (iso) => {
  const [y, m, d] = String(iso || '').split('-');
  return y && m && d ? `${d}/${m}` : iso;
};

// Sección: gráficas por día — evolución de lotes vs. ciclos (válidos y
// abortados), en cantidad y en kilos, para ubicar visualmente en qué días
// se concentra la diferencia del cuadre.
// Reutiliza los mismos datos ya cargados por `useKpisCuadre` (serie diaria de
// recepciones + listado crudo de ciclos), sin disparar llamadas nuevas.
const DailyChartsSection = ({ kpisCuadre }) => {
  const { data, loading, error, reload } = kpisCuadre;

  const rows = useMemo(() => {
    if (!data) return [];
    const { cycles = [], receptionDaily = [] } = data;

    const byDay = new Map();
    const ensure = (date) => {
      if (!byDay.has(date)) {
        byDay.set(date, {
          date,
          lotes: 0,
          kgLotes: 0,
          ciclosMaquina: 0,
          kgCiclosMaquina: 0,
          ciclosAbortados: 0,
          kgAbortadosNominal: 0,
        });
      }
      return byDay.get(date);
    };

    receptionDaily.forEach((r) => {
      const date = String(r?.date || '').slice(0, 10);
      if (!date) return;
      const row = ensure(date);
      row.lotes += Number(r?.count || 0);
      row.kgLotes += Number(r?.pesoKg || 0);
    });

    cycles.forEach((c) => {
      const date = String(c?.fecha || '').slice(0, 10);
      if (!date) return;
      const row = ensure(date);
      if (c?.abortado === true) {
        row.ciclosAbortados += Number(c.ciclos || 0);
        row.kgAbortadosNominal += Number(c.ciclos || 0) * Number(c.capacidad || 0);
      } else {
        row.ciclosMaquina += Number(c.ciclos || 0);
        row.kgCiclosMaquina += Number(c.kilosLavados || 0);
      }
    });

    return Array.from(byDay.values())
      .map((r) => ({
        ...r,
        diaLbl: fmtDate(r.date),
        ciclosValidos: Math.max(r.ciclosMaquina - r.ciclosAbortados, 0),
        kgCiclosValidos: Math.max(r.kgCiclosMaquina - r.kgAbortadosNominal, 0),
      }))
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  }, [data]);

  const manyDays = rows.length > 14;

  return (
    <SectionCard
      title="Gráficas por día"
      hint="Evolución diaria de lotes vs. ciclos válidos y abortados, en cantidad y en kilos."
      loading={loading}
      error={error}
      onReload={reload}
    >
      {!loading && rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
          Sin datos en el periodo seleccionado.
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div>
            <div className="text-[11px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 mb-2">
              Cantidad por día
            </div>
            <div className="h-[280px]">
              <ResponsiveContainer>
                <BarChart data={rows} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="diaLbl"
                    tick={{ fontSize: 11 }}
                    interval={manyDays ? 'preserveStartEnd' : 0}
                    angle={manyDays ? -45 : 0}
                    textAnchor={manyDays ? 'end' : 'middle'}
                    height={manyDays ? 45 : 25}
                  />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip formatter={(v) => fmtNum(v)} labelFormatter={(l) => `Día ${l}`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="lotes" name="Lotes" fill="#0F3460" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="ciclosValidos" name="Ciclos válidos" fill="#0E9F6E" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="ciclosAbortados" name="Abortados" fill="#FC536D" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div>
            <div className="text-[11px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400 mb-2">
              Kilos por día
            </div>
            <div className="h-[280px]">
              <ResponsiveContainer>
                <BarChart data={rows} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="diaLbl"
                    tick={{ fontSize: 11 }}
                    interval={manyDays ? 'preserveStartEnd' : 0}
                    angle={manyDays ? -45 : 0}
                    textAnchor={manyDays ? 'end' : 'middle'}
                    height={manyDays ? 45 : 25}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => `${fmtNum(v, 1)} kg`} labelFormatter={(l) => `Día ${l}`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="kgLotes" name="Kg lotes" fill="#0F3460" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="kgCiclosValidos" name="Kg ciclos válidos" fill="#0E9F6E" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
};

export default DailyChartsSection;
